import React, { useEffect, useState } from 'react';

import { StyledInputQuantity } from './InputQuantity.styles';

import { actionInput, InputQuantityProps } from './InputQuantity.types';

const InputQuantity = ({
  onChangeQuantity = () => {},
  label,
  quantity = 1,
  maxQuantity,
  minQuantity = '1',
  isSelect = false,
  isEditable = true,
  hideActionsWhenComponentIsNotEditable = true,
  componentClassname = 'input-quantity',
  customClassnames = '',
  customStyle = '',
  ...props
}: InputQuantityProps) => {
  const [inputQuantity, setInputQuantity] = useState<number>(quantity);
  const [temporaryInputValue, setTemporaryInputValue] = useState<number>();

  useEffect(() => {
    handleChange(quantity);
  }, [quantity]);

  /**
   * Allow value to be temporary below the minQuantity when user is typing
   */
  useEffect(() => {
    const timeout = setTimeout(() => handleChange(temporaryInputValue), 500);
    return () => clearTimeout(timeout);
  }, [temporaryInputValue]);

  const handleChangeWithDelay = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputQuantity(Number(e.target.value));
    setTemporaryInputValue(Number(e.target.value));
    onChangeQuantity(Number(e.target.value));
  };

  const handleChange = (value: number) => {
    if (value < Number(minQuantity)) {
      setInputQuantity(Number(minQuantity));
      onChangeQuantity(Number(minQuantity));
    }
    if (value > Number(maxQuantity)) {
      setInputQuantity(Number(maxQuantity));
      onChangeQuantity(Number(maxQuantity));
    }
  };

  const handleClick = (
    e:
      | (React.MouseEvent<HTMLButtonElement> & { type: 'click' })
      | (React.KeyboardEvent<HTMLButtonElement> & { type: 'keydown' }),
    action: actionInput,
  ) => {
    if (e.type == 'keydown' && !['enter', 'space'].includes(e.key)) return;
    if (
      [0, Number(minQuantity)].includes(inputQuantity) &&
      action === actionInput.MINUS
    )
      return;
    if (inputQuantity === Number(maxQuantity) && action === actionInput.PLUS)
      return;

    setInputQuantity((prev) =>
      action === actionInput.PLUS ? prev + 1 : prev - 1,
    );

    onChangeQuantity(
      action === actionInput.PLUS ? inputQuantity + 1 : inputQuantity - 1,
    );
  };

  const actionButton = (action: actionInput) => {
    if (hideActionsWhenComponentIsNotEditable && !isEditable) {
      return null;
    }

    return (
      <button
        className={`${componentClassname}__action__button-${action}`}
        onClick={(e: React.MouseEvent<HTMLButtonElement> & { type: 'click' }) =>
          handleClick(e, action)
        }
        onKeyDown={(
          e: React.KeyboardEvent<HTMLButtonElement> & { type: 'keydown' },
        ) => handleClick(e, action)}
        disabled={
          !isEditable ||
          (action === actionInput.MINUS
            ? [0, Number(minQuantity)].includes(inputQuantity)
            : inputQuantity === Number(maxQuantity))
        }
      >
        {action === actionInput.PLUS ? '+' : '-'}
      </button>
    );
  };

  return (
    <StyledInputQuantity
      className={`${componentClassname} ${customClassnames}`}
      customStyle={customStyle}
      isSelect={isSelect}
    >
      {label && <div className={`${componentClassname}__label`}>{label}</div>}

      {isSelect ? (
        <select
          className={`${componentClassname}__select`}
          disabled={!isEditable}
        >
          {Array(Number(maxQuantity ?? Math.max(5, inputQuantity)))
            .fill('')
            .map((_i, index) => (
              <option value={index + 1} selected={inputQuantity === index + 1}>
                {index + 1}
              </option>
            ))}
        </select>
      ) : (
        <div className={`${componentClassname}__action`}>
          {actionButton(actionInput.MINUS)}

          <input
            type="number"
            inputMode="numeric"
            max={maxQuantity}
            min={minQuantity}
            value={inputQuantity.toString()}
            onChange={handleChangeWithDelay}
            onBlur={(e) => handleChange(Number(e.target.value))}
            disabled={!isEditable}
            className={`${componentClassname}__action__input`}
            {...props}
          />

          {actionButton(actionInput.PLUS)}
        </div>
      )}
    </StyledInputQuantity>
  );
};
InputQuantity.displayName = 'InputQuantity';

export default InputQuantity;
